package br.com.htms.inspeccaocampo.shared.ui.components

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.IconButton
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextFieldColors
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.SoftwareKeyboardController
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import br.com.htms.inspeccaocampo.platform.PlatformServices

@Stable
class KeyboardFocusTracker {
    private var count by mutableIntStateOf(0)
    val hasFocus: Boolean get() = count > 0

    fun gain() {
        count++
    }

    fun lose() {
        count = (count - 1).coerceAtLeast(0)
    }
}

val LocalKeyboardFocusTracker = staticCompositionLocalOf { KeyboardFocusTracker() }

fun dismissKeyboard(
    focusManager: FocusManager,
    keyboard: SoftwareKeyboardController?
) {
    keyboard?.hide()
    focusManager.clearFocus(force = true)
    PlatformServices.dismissSoftKeyboard()
}

/**
 * Envolve o app: rolagem com campo focado fecha o teclado.
 * Sem overlay clicável em tela cheia — no Web isso bloqueava wheel/trackpad
 * do LazyColumn. Use Enter/Done ou o OK no campo ([AppOutlinedTextField]).
 */
@Composable
fun KeyboardDismissHost(
    content: @Composable () -> Unit
) {
    val tracker = remember { KeyboardFocusTracker() }
    val focusManager = LocalFocusManager.current
    val keyboard = LocalSoftwareKeyboardController.current

    val clearOnScroll = remember(focusManager, keyboard, tracker) {
        object : NestedScrollConnection {
            override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
                // Nunca consome delta (Offset.Zero) — só fecha o teclado.
                if (available != Offset.Zero && tracker.hasFocus) {
                    dismissKeyboard(focusManager, keyboard)
                }
                return Offset.Zero
            }
        }
    }

    CompositionLocalProvider(LocalKeyboardFocusTracker provides tracker) {
        // nestedScroll só com foco: evita disputa com LazyColumn no wheel/PC.
        // Tab no host: no Web o browser/canvas às vezes não entrega Key.Tab ao TextField.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .onPreviewKeyEvent { event ->
                    if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                    if (event.key != Key.Tab) return@onPreviewKeyEvent false
                    val primary =
                        if (event.isShiftPressed) FocusDirection.Previous
                        else FocusDirection.Next
                    val fallback =
                        if (event.isShiftPressed) FocusDirection.Up
                        else FocusDirection.Down
                    if (!focusManager.moveFocus(primary)) {
                        focusManager.moveFocus(fallback)
                    }
                    true
                }
                .then(
                    if (tracker.hasFocus) Modifier.nestedScroll(clearOnScroll)
                    else Modifier
                )
        ) {
            content()
        }
    }
}

/**
 * OutlinedTextField do app:
 * - Tab / Shift+Tab (e ImeAction.Next) navegam entre campos no PC/web
 * - Enter/Done e botão OK fecham o teclado (mobile)
 */
@Composable
fun AppOutlinedTextField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    textStyle: TextStyle = LocalTextStyle.current,
    label: @Composable (() -> Unit)? = null,
    placeholder: @Composable (() -> Unit)? = null,
    leadingIcon: @Composable (() -> Unit)? = null,
    trailingIcon: @Composable (() -> Unit)? = null,
    prefix: @Composable (() -> Unit)? = null,
    suffix: @Composable (() -> Unit)? = null,
    supportingText: @Composable (() -> Unit)? = null,
    isError: Boolean = false,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = false,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    shape: Shape = OutlinedTextFieldDefaults.shape,
    colors: TextFieldColors = FormFieldDefaults.textFieldColors()
) {
    val focusManager = LocalFocusManager.current
    val keyboard = LocalSoftwareKeyboardController.current
    val tracker = LocalKeyboardFocusTracker.current
    var wasFocused by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        onDispose {
            if (wasFocused) tracker.lose()
        }
    }

    // Single-line: Next (avança campo). Multiline / Done explícito: mantém Done.
    val effectiveOptions = when {
        keyboardOptions.imeAction != ImeAction.Default -> keyboardOptions
        singleLine -> keyboardOptions.copy(imeAction = ImeAction.Next)
        else -> keyboardOptions.copy(imeAction = ImeAction.Done)
    }

    val effectiveActions = KeyboardActions(
        onDone = {
            keyboardActions.onDone?.invoke(this)
            dismissKeyboard(focusManager, keyboard)
        },
        onGo = {
            keyboardActions.onGo?.invoke(this)
            dismissKeyboard(focusManager, keyboard)
        },
        onSearch = {
            keyboardActions.onSearch?.invoke(this)
            dismissKeyboard(focusManager, keyboard)
        },
        onSend = {
            keyboardActions.onSend?.invoke(this)
            dismissKeyboard(focusManager, keyboard)
        },
        onPrevious = {
            keyboardActions.onPrevious?.invoke(this)
            focusManager.moveFocus(FocusDirection.Previous)
        },
        onNext = {
            keyboardActions.onNext?.invoke(this)
            focusManager.moveFocus(FocusDirection.Next)
        }
    )

    val resolvedTrailing: @Composable (() -> Unit)? = when {
        trailingIcon != null -> trailingIcon
        wasFocused && enabled && !readOnly -> {
            {
                // canFocus=false: senão Tab vai para o OK (mesmo campo) e
                // parece que só Shift+Tab funciona no PC/Web.
                IconButton(
                    onClick = { dismissKeyboard(focusManager, keyboard) },
                    modifier = Modifier
                        .size(40.dp)
                        .focusProperties { canFocus = false }
                ) {
                    Text(
                        "OK",
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(2.dp)
                    )
                }
            }
        }
        else -> null
    }

    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier
            .onPreviewKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                when (event.key) {
                    Key.Tab -> {
                        val primary =
                            if (event.isShiftPressed) FocusDirection.Previous
                            else FocusDirection.Next
                        val fallback =
                            if (event.isShiftPressed) FocusDirection.Up
                            else FocusDirection.Down
                        if (!focusManager.moveFocus(primary)) {
                            focusManager.moveFocus(fallback)
                        }
                        true
                    }
                    // Enter em campo de uma linha com Next → próximo campo (PC).
                    Key.Enter, Key.NumPadEnter -> {
                        if (!singleLine) return@onPreviewKeyEvent false
                        if (effectiveOptions.imeAction == ImeAction.Done) {
                            false
                        } else {
                            focusManager.moveFocus(FocusDirection.Next)
                            true
                        }
                    }
                    else -> false
                }
            }
            .onFocusChanged { state ->
                when {
                    state.isFocused && !wasFocused -> {
                        wasFocused = true
                        tracker.gain()
                    }
                    !state.isFocused && wasFocused -> {
                        wasFocused = false
                        tracker.lose()
                    }
                }
            },
        enabled = enabled,
        readOnly = readOnly,
        textStyle = textStyle,
        label = label,
        placeholder = placeholder,
        leadingIcon = leadingIcon,
        trailingIcon = resolvedTrailing,
        prefix = prefix,
        suffix = suffix,
        supportingText = supportingText,
        isError = isError,
        visualTransformation = visualTransformation,
        keyboardOptions = effectiveOptions,
        keyboardActions = effectiveActions,
        singleLine = singleLine,
        maxLines = maxLines,
        minLines = minLines,
        interactionSource = interactionSource,
        shape = shape,
        colors = colors
    )
}
